import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Switch,
  Link,
  IconButton
} from '@mui/material';
import { ShowChart } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../../utils/api';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom icons for active/inactive stations
const activeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const inactiveIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

function Stations() {
  const navigate = useNavigate();
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Fetch weather stations
  const fetchStations = async () => {
    try {
      setLoading(true);
      const data = await api.get('/weather-stations');
      setStations(data.stations || []);
    } catch (err) {
      console.error('Error fetching stations:', err);
      setError('Failed to load weather stations');
    } finally {
      setLoading(false);
    }
  };

  // Update station status
  const updateStationStatus = async (stationId, active) => {
    try {
      setUpdating(true);
      const data = await api.patch(`/weather-stations/${stationId}/status`, { active });
      
      // Update the stations list
      setStations(prevStations => 
        prevStations.map(station => 
          station.stationId === stationId 
            ? { ...station, active } 
            : station
        )
      );

      // Update selected station if it's the one being updated
      if (selectedStation?.stationId === stationId) {
        setSelectedStation({ ...selectedStation, active });
      }

      setSnackbar({
        open: true,
        message: data.message,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating station:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update station status',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchStations();
  }, []);

  const handleMarkerClick = (station) => {
    setSelectedStation(station);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStation(null);
  };

  const handleToggleStatus = () => {
    if (selectedStation) {
      updateStationStatus(selectedStation.stationId, !selectedStation.active);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleToggleActiveOnly = (event) => {
    setShowActiveOnly(event.target.checked);
  };

  // Filter stations based on showActiveOnly setting
  const filteredStations = showActiveOnly ? stations.filter(station => station.active) : stations;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h4" gutterBottom>
            Weather Stations
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showActiveOnly}
                onChange={handleToggleActiveOnly}
                color="primary"
              />
            }
            label="Show Active Only"
            sx={{ ml: 2 }}
          />
        </Box>
        <Typography variant="body1" color="text.secondary">
          {showActiveOnly ? 
            `${filteredStations.length} active stations displayed` :
            `${stations.length} total stations • ${stations.filter(s => s.active).length} active • ${stations.filter(s => !s.active).length} inactive`
          }
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <MapContainer
          center={[39.8283, -98.5795]} // Center of USA
          zoom={5}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {filteredStations.map((station) => (
            <Marker
              key={station.stationId}
              position={[station.latitude, station.longitude]}
              icon={station.active ? activeIcon : inactiveIcon}
              eventHandlers={{
                click: () => handleMarkerClick(station)
              }}
            />
          ))}
        </MapContainer>
      </Box>

      {/* Station Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Weather Station: {selectedStation?.stationId}
          <IconButton
            onClick={() => navigate(`/biometric-activity/${selectedStation?.stationId}`, { state: { from: '/stations' } })}
            sx={{ color: 'primary.main', width: 'auto' }}
            aria-label="View Biometric Activity"
          >
            <ShowChart />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedStation && (
            <>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={12}>
                <Chip
                  label={selectedStation.active ? 'Active' : 'Inactive'}
                  color={selectedStation.active ? 'success' : 'default'}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Latitude
                </Typography>
                <Typography variant="body1">
                  {selectedStation.latitude}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Longitude
                </Typography>
                <Typography variant="body1">
                  {selectedStation.longitude}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Primary BUFKIT URL
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                  {selectedStation.bufkitUrl1 || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Secondary BUFKIT URL
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                  {selectedStation.bufkitUrl2 || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} paddingTop={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Event Date
                </Typography>
                <Typography variant="body1">
                  {selectedStation.lastEventDate || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Forecast
                </Typography>
                <Typography variant="body1">
                  {selectedStation.lastForecastDatetime ? 
                    selectedStation.lastForecastDatetime.replace('T', ' ').replace(':00+00:00', ' EST') : 
                    'N/A'
                  }
                </Typography>
              </Grid>
            </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
          <Button
            onClick={handleToggleStatus}
            variant="contained"
            color={selectedStation?.active ? 'error' : 'success'}
            disabled={updating}
            startIcon={updating && <CircularProgress size={20} />}
          >
            {updating ? 'Updating...' : (selectedStation?.active ? 'Deactivate' : 'Activate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Stations;
